import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthenticationService, OrderService, RfidProductionService } from 'app/services';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from 'app/i18n';
import { SET_JOB_DETAILS_VIEW } from 'ngrx/actions';
import { LAMINATED, LAMINATING, VALIDATING, RFID, CUT } from 'ngrx/status-types';
import { BsModalService } from 'ngx-bootstrap';
import { Observable, Subscription } from 'rxjs';

interface QACheckConfig {
  title: string;
  nextStatus: string;
  previousStatus: string;
  checkpoints: Array<{
    key: string;
    translationKey: string;
  }>;
  activityNote: string;
}

@Component({
  selector: 'rfid-qa-check',
  templateUrl: 'rfid-qa-check.component.html',
  styleUrls: ['rfid-qa-check.component.scss']
})
export class RFIDQACheck implements OnInit, OnDestroy {
  @Input('status') status;
  @Input('job') job;
  @Input('jobs') jobs = [];
  
  process: Observable<string>;
  isAdmin: boolean;
  isSupervisor: boolean;
  category: string;
  initSubscriptions: Subscription[];
  qaCheckForm: FormGroup;
  qaConfig: QACheckConfig;

  // Configuration for different QA check types
  qaCheckConfigs = {
    'laminatingCheck': {
      title: 'LAMINATOR_QA_CHECK',
      nextStatus: LAMINATED,
      previousStatus: LAMINATING,
      checkpoints: [
        { key: 'checkLaminationQuality', translationKey: 'CHECK_LAMINATION_QUALITY' },
        { key: 'checkNoAirBubbles', translationKey: 'CHECK_NO_AIR_BUBBLES' }
      ],
      activityNote: 'Laminator QA check completed'
    },
    'validatingCheck': {
      title: 'VALIDATING_QA_CHECK',
      nextStatus: VALIDATING,
      previousStatus: CUT,
      checkpoints: [
        { key: 'checkSmoothEdges', translationKey: 'CHECK_SMOOTH_EDGES' },
        { key: 'checkPunchAlignment', translationKey: 'CHECK_PUNCH_ALIGNMENT' }
      ],
      activityNote: 'Validating QA check completed'
    }
    // Add more check types as needed
  };

  constructor(
    private authService: AuthenticationService,
    private fb: FormBuilder,
    private translate: TranslateService,
    public modalService: BsModalService,
    private orderService: OrderService,
    private store: Store<any>,
    private rfidProductionService: RfidProductionService
  ) {
    this.process = this.authService.getCurrentProcess();
    this.isAdmin = this.authService.userIsAdmin();
    this.isSupervisor = this.authService.userIsSupervisor();
  }

  ngOnInit(): void {
    // console.log('status', this.status);
    // console.log('job', this.job);

    // Load the configuration for the current status
    this.qaConfig = this.qaCheckConfigs[this.status];

    // If a single job was passed, add it to the jobs array for consistent processing
    if (this.job && this.jobs.length === 0) {
      this.jobs = [this.job];
    }

    this.initSubscriptions = [
      this.process.subscribe(process => {
        this.category = process;
      })
    ];

    this.createForms();
  }

  ngOnDestroy(): void {
    if (this.initSubscriptions) {
      this.initSubscriptions.forEach(sub => sub.unsubscribe());
    }
  }

  createForms(): void {
    // Dynamically create form controls based on checkpoints
    const formControls = {};
    
    if (this.qaConfig && this.qaConfig.checkpoints) {
      this.qaConfig.checkpoints.forEach(checkpoint => {
        formControls[checkpoint.key] = [false, [Validators.requiredTrue]];
      });
    } else {
      // Fallback to default controls
      formControls['checkLaminationQuality'] = [false, [Validators.requiredTrue]];
      formControls['checkNoAirBubbles'] = [false, [Validators.requiredTrue]];
    }
    
    this.qaCheckForm = this.fb.group(formControls);
  }

  updateJob(): void {
    this.modalService.hide(1);
    
    this.store.dispatch({
      type: SET_JOB_DETAILS_VIEW,
      payload: { loading: true }
    });

    const user = JSON.parse(localStorage.getItem('currentUser'));
    const timestamp = new Date().getTime().toString();

    if (this.category === RFID && this.qaConfig) {
      // Process all jobs
      this.jobs.forEach(job => {
        const updatedJob = Object.assign({}, job, {
          activity_history: [{
            activity: this.qaConfig.nextStatus,
            note: this.translate.instant('JOB_STATUS_UPDATED'),
            timestamp,
            user: user.email
          }, ...job.activity_history]
        });

        updatedJob.status = this.qaConfig.nextStatus;
        updatedJob.previous_status = this.qaConfig.previousStatus;

        updatedJob.activity_history.unshift({
          activity: 'QA Check',
          note: this.qaConfig.activityNote,
          timestamp,
          user: user.email
        });

        this.orderService.updateJob(updatedJob);

        if(this.qaConfig.nextStatus == LAMINATED){
          // Create the job input object from job data
          const jobData = {
            id: job.id,
            orderId: job.orderId || '',
            rfid_code: job.rfid_code || '',
            rfid_chipType: job.rfid_chipType || '',
            rfid_tech: job.rfid_tech || '',
            rfid_finishType: job.rfid_finishType || '',
            qty: job.qty || 0,
            reprintCount: job.reprintCount || 0,
            rfid_inlayPartNumber: job.rfid_inlayPartNumber || job.inlay_part_number || '',
            product: job.product.sku || job.product || '',
            sheets: job.sheets || 0
          };
        
          // Get sheet counts from artwork data
          let frontSheetCount = 0;
          let backSheetCount = 0;
          
          if (job.rfid_artworks && job.rfid_artworks.front && job.rfid_artworks.back) {
            frontSheetCount = job.rfid_artworks.front.sheet_print_qty || 0;
            backSheetCount = job.rfid_artworks.back.sheet_print_qty || 0;
          }
          
          // Sheet dimensions: 18" x 12"
          const SHEET_WIDTH_INCHES = 18;
          const SHEET_HEIGHT_INCHES = 12;
          
          // Calculate square feet per sheet
          // 1 square foot = 144 square inches (12" x 12")
          const squareFeetPerSheet = (SHEET_WIDTH_INCHES * SHEET_HEIGHT_INCHES) / 144;
          
          // Calculate total square feet of laminate used
          const frontLaminateFeet = frontSheetCount * squareFeetPerSheet;
          const backLaminateFeet = backSheetCount * squareFeetPerSheet;
          
          console.log(`Sheet size: ${SHEET_WIDTH_INCHES}" x ${SHEET_HEIGHT_INCHES}" = ${squareFeetPerSheet} sq ft per sheet`);
          console.log(`Front sheets: ${frontSheetCount} = ${frontLaminateFeet} sq ft`);
          console.log(`Back sheets: ${backSheetCount} = ${backLaminateFeet} sq ft`);
          
          // Get laminate type and ID from job if available
          const laminateType = job.rfid_finishType || '';
          
          // Consumption data for laminate in square feet
          const consumption = {
            front_laminate_feet_used: 0, // Set to 0 as we only track scrap now, this gets populated in the backend on creation
            back_laminate_feet_used: 0, // Set to 0 as we only track scrap now, this gets populated in the backend on creation
            front_laminate_scrap: 0, // Default to 0 scrap
            back_laminate_scrap: 0, // Default to 0 scrap
            laminate_type: laminateType
          };

          console.log(`Recording laminate consumption for job ${job.id}: Front: ${frontLaminateFeet} sq ft, Back: ${backLaminateFeet} sq ft`);
          
          this.rfidProductionService.recordLaminateConsumption(
            job.id,
            jobData,
            consumption
          ).subscribe(
            result => {
              console.log('Successfully recorded laminate consumption:', result);
            },
            error => {
              console.error('Error recording laminate consumption:', error);
              // Optionally show an error notification to the user, but don't block the workflow
            }
          );
        }
      });

      this.store.dispatch({
        type: SET_JOB_DETAILS_VIEW,
        payload: { loading: false }
      });
    }
  }
}



// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/RFID/rfid-qa-check/rfid-qa-check.component.ts